import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  DialogContentText,
  Collapse,
  ListItemButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutline from "@mui/icons-material/HelpOutline";
import ManageAccounts from "@mui/icons-material/ManageAccounts";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SupportIcon from "@mui/icons-material/Support";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo"; // Icon for Webinar
import MyAccountDialog from "./MyAccountDialog";
import { useAuth } from "../context/AuthContext";
import EmailIcon from "@mui/icons-material/Email";
import CircularProgress from "@mui/material/CircularProgress";
import { sendSupportEmailToAdmin, deleteAccount, changeUserEmail } from "../networking/AccountCalls";
// Import PDFs from src folder
import CreatingASavedSearch from "../Creating_a_Saved_Search.pdf";
import FilterSearchingResearchingSolicitations from "../Filter_Searching_Researching_Solicitations.pdf";
import ResearchingCompaniesContracts from "../Researching_Companies_Contracts_and_Task_Orders_Using_Quantum.pdf";
import ResearchingContractsByAgency from "../Researching_Contracts_by_Agency.pdf";

const RightDrawer = () => {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false); // For video popup
  const [selectedPdf, setSelectedPdf] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [changeEmailDialogOpen, setChangeEmailDialogOpen] = useState(false);
  const [changeEmailCurrentEmail, setChangeEmailCurrentEmail] = useState("");
  const [changeEmailPassword, setChangeEmailPassword] = useState("");
  const [changeEmailNewEmail, setChangeEmailNewEmail] = useState("");
  const [changeEmailConfirmEmail, setChangeEmailConfirmEmail] = useState("");
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);
  const [changeEmailError, setChangeEmailError] = useState("");
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();

  // Real Firebase Storage URL for the webinar video
  const webinarVideoUrl =
    "https://firebasestorage.googleapis.com/v0/b/quantum-6c796.appspot.com/o/quantum_webinar_and_demo%20(360p).mp4?alt=media&token=05fcc348-2c2e-4c65-93b3-eb0db77884e7";

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpen(open);
  };

  const handleInviteClick = () => {
    navigate("/invite");
    setOpen(false);
  };

  const handleAccountClick = () => {
    setAccountDialogOpen(true);
  };

  const handleCloseAccountDialog = () => {
    setAccountDialogOpen(false);
  };

  const handleSignOutClick = () => {
    setDialogOpen(true);
  };

  const handleSignOutConfirm = () => {
    logout();
    setDialogOpen(false);
    setOpen(false);
  };

  const handleSignOutCancel = () => {
    setDialogOpen(false);
  };

  const handleDeleteAccountClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteAccountConfirm = async () => {
    try {
      await deleteAccount(currentUser);
      await logout();
    } catch (error) {
      console.error("Error deleting account:", error);
    }
    setDeleteDialogOpen(false);
    setOpen(false);
  };

  const handleDeleteAccountCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleChangeEmailClick = () => {
    setChangeEmailDialogOpen(true);
  };

  const handleChangeEmailCancel = () => {
    setChangeEmailDialogOpen(false);
    setChangeEmailCurrentEmail("");
    setChangeEmailPassword("");
    setChangeEmailNewEmail("");
    setChangeEmailConfirmEmail("");
    setChangeEmailError("");
  };

  const handleChangeEmailSubmit = async () => {
    setChangeEmailError("");

    // Validate all fields are filled
    if (!changeEmailCurrentEmail.trim() || !changeEmailNewEmail.trim() || !changeEmailConfirmEmail.trim() || !changeEmailPassword.trim()) {
      setChangeEmailError("Please fill in all fields.");
      return;
    }

    // Validate current email matches
    if (changeEmailCurrentEmail.trim().toLowerCase() !== currentUser.email.toLowerCase()) {
      setChangeEmailError("Current email does not match your account email.");
      return;
    }

    // Validate new emails match (case-insensitive)
    if (changeEmailNewEmail.trim().toLowerCase() !== changeEmailConfirmEmail.trim().toLowerCase()) {
      setChangeEmailError("New email addresses do not match.");
      return;
    }

    // Validate new email is different from current
    if (changeEmailNewEmail.trim().toLowerCase() === currentUser.email.toLowerCase()) {
      setChangeEmailError("New email must be different from your current email.");
      return;
    }

    setChangeEmailLoading(true);
    try {
      const message = await changeUserEmail(changeEmailPassword, changeEmailNewEmail);
      alert(message);
      handleChangeEmailCancel();
    } catch (error) {
      console.error("Change email failed:", error);
      if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        setChangeEmailError("Incorrect password. Please try again.");
      } else if (error.code === "auth/email-already-in-use") {
        setChangeEmailError("This email is already in use by another account.");
      } else if (error.code === "auth/invalid-email") {
        setChangeEmailError("Please enter a valid email address.");
      } else if (error.code === "auth/too-many-requests") {
        setChangeEmailError("Too many attempts. Please try again later.");
      } else {
        setChangeEmailError(error.message || "Failed to change email. Please try again.");
      }
    } finally {
      setChangeEmailLoading(false);
    }
  };

  const handleSupportClick = () => {
    setSupportDialogOpen(true);
  };

  const handleSupportSend = async () => {
    if (!supportMessage.trim()) {
      alert("Please enter a message.");
      return;
    }
    try {
      await sendSupportEmailToAdmin(currentUser.email, supportMessage);
      alert("Support request sent successfully.");
      setSupportDialogOpen(false);
      setSupportMessage("");
    } catch (error) {
      console.error("Error sending support request:", error);
      alert("Failed to send support request.");
    }
  };

  const handleSupportCancel = () => {
    setSupportDialogOpen(false);
    setSupportMessage("");
  };

  const handleHelpCenterClick = () => {
    setHelpCenterOpen(!helpCenterOpen);
  };

  const handlePdfClick = (pdfFile) => {
    setSelectedPdf(pdfFile);
    setPdfDialogOpen(true);
    setOpen(false);
  };

  const handleVideoClick = () => {
    setVideoDialogOpen(true);
    setOpen(false);
  };

  const handlePdfDialogClose = () => {
    setPdfDialogOpen(false);
    setSelectedPdf("");
  };

  const handleVideoDialogClose = () => {
    setVideoDialogOpen(false);
  };

  const list1 = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {/* Help Center ListItem */}
        <ListItem button onClick={handleHelpCenterClick}>
          <ListItemIcon>
            <SupportIcon />
          </ListItemIcon>
          <ListItemText primary="Help Center" />
          {helpCenterOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={helpCenterOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() => handlePdfClick(CreatingASavedSearch)}
            >
              <ListItemText primary="Creating a Saved Search" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() =>
                handlePdfClick(FilterSearchingResearchingSolicitations)
              }
            >
              <ListItemText primary="Filter Searching Researching Solicitations" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() => handlePdfClick(ResearchingCompaniesContracts)}
            >
              <ListItemText primary="Researching Companies Contracts and Task Orders Using Quantum" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() => handlePdfClick(ResearchingContractsByAgency)}
            >
              <ListItemText primary="Researching Contracts by Agency" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Webinar ListItem (standalone, below Help Center) */}
        <ListItem button onClick={handleVideoClick}>
          <ListItemIcon>
            <OndemandVideoIcon />
          </ListItemIcon>
          <ListItemText primary="Webinar" />
        </ListItem>

        {/* Existing ListItems */}
        <ListItem button onClick={handleInviteClick}>
          <ListItemIcon>
            <PersonAddIcon />
          </ListItemIcon>
          <ListItemText primary="Invite" />
        </ListItem>
        {/*
        <ListItem button onClick={handleAccountClick}>
          <ListItemIcon>
            <ManageAccounts />
          </ListItemIcon>
          <ListItemText primary="My Account" />
        </ListItem>
        */}
        <ListItem button onClick={handleChangeEmailClick}>
          <ListItemIcon>
            <EmailIcon />
          </ListItemIcon>
          <ListItemText primary="Change Email" />
        </ListItem>
        <ListItem button onClick={handleSupportClick}>
          <ListItemIcon>
            <HelpOutline />
          </ListItemIcon>
          <ListItemText primary="Request Support" />
        </ListItem>
        <ListItem button onClick={handleSignOutClick}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItem>
        <ListItem button onClick={handleDeleteAccountClick}>
          <ListItemIcon>
            <DeleteForeverIcon />
          </ListItemIcon>
          <ListItemText primary="Delete Account" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <div>
      <IconButton
        edge="end"
        color="inherit"
        aria-label="menu"
        onClick={toggleDrawer(true)}
      >
        <MenuIcon />
      </IconButton>
      <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
        {list1}
      </Drawer>

      {/* Confirmation Dialog for Sign Out */}
      <Dialog
        open={dialogOpen}
        onClose={handleSignOutCancel}
        aria-labelledby="sign-out-dialog-title"
        aria-describedby="sign-out-dialog-description"
      >
        <DialogTitle id="sign-out-dialog-title">{"Sign Out"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="sign-out-dialog-description">
            Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSignOutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSignOutConfirm} color="primary" autoFocus>
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Delete Account */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteAccountCancel}
        aria-labelledby="delete-account-dialog-title"
        aria-describedby="delete-account-dialog-description"
      >
        <DialogTitle id="delete-account-dialog-title">{"Delete Account"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-account-dialog-description">
            Are you sure you want to permanently delete your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAccountCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAccountConfirm} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support Request Dialog */}
      <Dialog
        open={supportDialogOpen}
        onClose={handleSupportCancel}
        aria-labelledby="support-dialog-title"
      >
        <DialogTitle id="support-dialog-title">{"Request Support"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the details of your issue or question, and we will get
            back to you as soon as possible.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="supportMessage"
            label="Support Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSupportCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSupportSend} color="primary">
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog
        open={changeEmailDialogOpen}
        onClose={handleChangeEmailCancel}
        aria-labelledby="change-email-dialog-title"
      >
        <DialogTitle id="change-email-dialog-title">{"Change Email"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your current email, password, and new email address. A
            verification link will be sent to your new email to complete the
            change.
          </DialogContentText>
          {changeEmailError && (
            <DialogContentText sx={{ color: "error.main", mt: 1 }}>
              {changeEmailError}
            </DialogContentText>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Current Email"
            type="email"
            fullWidth
            value={changeEmailCurrentEmail}
            onChange={(e) => setChangeEmailCurrentEmail(e.target.value)}
            disabled={changeEmailLoading}
          />
          <TextField
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            value={changeEmailPassword}
            onChange={(e) => setChangeEmailPassword(e.target.value)}
            disabled={changeEmailLoading}
          />
          <TextField
            margin="dense"
            label="New Email"
            type="email"
            fullWidth
            value={changeEmailNewEmail}
            onChange={(e) => setChangeEmailNewEmail(e.target.value)}
            disabled={changeEmailLoading}
          />
          <TextField
            margin="dense"
            label="Confirm New Email"
            type="email"
            fullWidth
            value={changeEmailConfirmEmail}
            onChange={(e) => setChangeEmailConfirmEmail(e.target.value)}
            disabled={changeEmailLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangeEmailCancel} color="primary" disabled={changeEmailLoading}>
            Cancel
          </Button>
          <Button onClick={handleChangeEmailSubmit} color="primary" disabled={changeEmailLoading}>
            {changeEmailLoading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handlePdfDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Help Center Document</DialogTitle>
        <DialogContent>
          <iframe
            src={selectedPdf}
            style={{ width: "100%", height: "500px", border: "none" }}
            title="Help Center PDF"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePdfDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Viewer Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleVideoDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Quantum Webinar</DialogTitle>
        <DialogContent>
          <video
            controls
            style={{ width: "100%", maxHeight: "500px" }}
            src={webinarVideoUrl}
          >
            Your browser does not support the video tag.
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVideoDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* My Account Dialog */}
      <MyAccountDialog
        open={accountDialogOpen}
        onClose={handleCloseAccountDialog}
      />
    </div>
  );
};

export default RightDrawer;
